import type { Ref } from '@/composition/vue/compositionApi'
import { onScopeDispose, ref, watch } from '@/composition/vue/compositionApi'
import { useProvideInject } from '@/composition/provideInject'
import { keyCodes } from '@/constants/keyCodes'

const dropdownSymbol = Symbol('tm-dropdown')

export const useInsideDropdown = () => useProvideInject<boolean>(dropdownSymbol)

const dropdownCloseOnScrollSymbol = Symbol('tm-dropdown-close-on-scroll')

export const useDropdownCloseOnScrollContext = () => useProvideInject<boolean>(dropdownCloseOnScrollSymbol)

export type OpenDropdownEntry = {
  contains: (el: Element) => boolean
  close: () => void
}

// Quasar closes menus only on mousedown/touchstart, so keyboard-triggered opens need explicit closing of others
const openDropdownEntries = new Set<OpenDropdownEntry>()

type OpenDropdownRegistry = {
  registerAndCloseUnrelated: (anchorEl: Element) => void
  unregister: () => void
}

export const useOpenDropdownRegistry = (entry: OpenDropdownEntry): OpenDropdownRegistry => {
  // simulates outside-click: keep dropdowns whose menu contains the new anchor (nested case)
  const registerAndCloseUnrelated = (anchorEl: Element): void => {
    openDropdownEntries.forEach((other) => {
      if (other !== entry && !other.contains(anchorEl)) {
        other.close()
      }
    })
    openDropdownEntries.add(entry)
  }

  const unregister = (): void => {
    openDropdownEntries.delete(entry)
  }

  onScopeDispose(unregister)

  return { registerAndCloseUnrelated, unregister }
}

type DropdownOptionsNavigationParams = {
  isOpen: Ref<boolean>
  optionsCount: Ref<number>
  getOptionElement: (index: number) => HTMLElement | undefined
  close: () => void
}

type DropdownOptionsNavigation = {
  focusedIndex: Ref<number>
  resetFocusedOption: () => void
}

type DropdownKeydownHandler = (event: KeyboardEvent) => void

const openNavigations = new Set<DropdownKeydownHandler>()

const onDocumentKeydown = (event: KeyboardEvent) => {
  openNavigations.forEach((handler) => handler(event))
}

const registerOpenNavigation = (handler: DropdownKeydownHandler) => {
  if (openNavigations.size === 0) {
    document.addEventListener('keydown', onDocumentKeydown)
  }
  openNavigations.add(handler)
}

const unregisterOpenNavigation = (handler: DropdownKeydownHandler) => {
  if (!openNavigations.delete(handler) || openNavigations.size > 0) {
    return
  }
  document.removeEventListener('keydown', onDocumentKeydown)
}

// Quasar toggles a menu on Enter keyup on its anchor, and the anchor is refocused as soon as the
// menu hides — so the keyup that follows a keyboard selection has to be swallowed, otherwise it
// immediately re-opens the dropdown. The listener is one-shot: the dropdown is already closing.
const swallowNextEnterKeyup = () => {
  const onKeyup = (event: KeyboardEvent) => {
    document.removeEventListener('keyup', onKeyup, true)
    if (event.key === keyCodes.keyEnter) {
      event.stopPropagation()
      event.preventDefault()
    }
  }
  document.addEventListener('keyup', onKeyup, true)
}

export const useDropdownOptionsNavigation = ({
  isOpen,
  optionsCount,
  getOptionElement,
  close,
}: DropdownOptionsNavigationParams): DropdownOptionsNavigation => {
  const focusedIndex = ref(-1)

  const resetFocusedOption = () => {
    focusedIndex.value = -1
  }

  const moveFocusedOption = (offset: 1 | -1) => {
    const count = optionsCount.value
    if (count === 0) {
      return
    }

    focusedIndex.value =
      focusedIndex.value === -1 && offset === -1 ? count - 1 : (focusedIndex.value + offset + count) % count

    getOptionElement(focusedIndex.value)?.scrollIntoView({ block: 'nearest' })
  }

  const selectFocusedOption = () => {
    const optionElement = getOptionElement(focusedIndex.value)
    if (!optionElement) {
      return false
    }

    swallowNextEnterKeyup()
    optionElement.click()
    return true
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === keyCodes.keyDown || event.key === keyCodes.keyUp) {
      event.preventDefault()
      moveFocusedOption(event.key === keyCodes.keyUp ? -1 : 1)
      return
    }

    if (event.key === keyCodes.keyEnter && selectFocusedOption()) {
      event.preventDefault()
      return
    }

    if (event.key === keyCodes.keyTab) {
      close()
    }
  }

  watch(
    isOpen,
    (value) => {
      if (value) {
        registerOpenNavigation(onKeydown)
        return
      }
      unregisterOpenNavigation(onKeydown)
      resetFocusedOption()
    },
    { immediate: true, flush: 'sync' },
  )

  onScopeDispose(() => unregisterOpenNavigation(onKeydown))

  return { focusedIndex, resetFocusedOption }
}
